import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Autocomplete, TextField, CircularProgress, Box, Typography,
} from '@mui/material';
import { AddCircleOutline as CreateIcon } from '@mui/icons-material';
import debounce from 'lodash/debounce';
import wineService from '../../services/wineService';
import type { Wine, WineSearchResult } from '../../services/wineService';
import { useAuth } from '../../context/AuthContext';
import RequestWineModal from './RequestWineModal';
import { wineColorDotFor, normalizeWineColor, NEUTRAL_WINE_DOT } from '../../utils/wineColor';

export type { WineSearchResult };

// Sentinel option appended at the bottom of the dropdown
const CREATE_OPTION_ID = '__create_wine__';
type CreateOption = { _id: typeof CREATE_OPTION_ID; name: string; __isCreate__: true };
type DropdownOption = WineSearchResult | CreateOption;

const isCreateOption = (o: DropdownOption): o is CreateOption =>
  (o as CreateOption).__isCreate__ === true;

interface Props {
  value: WineSearchResult | null;
  onChange: (wine: WineSearchResult | null) => void;
  onCreateNew?: (query: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  label?: string;
  /**
   * When provided, search hits the venue-scoped lookup endpoint
   * (`/admin/venue/:venueId/wine-lookup/search`) which is open to VENUE_USER.
   * Omit only from admin-context callers operating outside any venue.
   */
  venueId?: string;
}

const WineSearchBox: React.FC<Props> = ({
  value,
  onChange,
  onCreateNew,
  error,
  helperText,
  disabled,
  label = 'Wine',
  venueId,
}) => {
  const { isVenueUser } = useAuth();
  // Two creation modes share the same sentinel slot:
  //   - VENUE_USER inside a venue (venueId present) → opens RequestWineModal
  //     internally. The result is a provisional wine (needsReview, source =
  //     'venue_request') wired straight back through onChange.
  //   - ADMIN/SYSTEM_ADMIN with a parent-provided onCreateNew → fires the
  //     callback, parent owns CreateWineModal as before.
  // Both modes require that the parent expressed creation intent (passed
  // onCreateNew); WineSearchBox itself never invents one.
  const venueRequestMode = !!onCreateNew && isVenueUser() && !!venueId;
  const adminCreateMode = !!onCreateNew && !isVenueUser();
  const canCreate = venueRequestMode || adminCreateMode;

  const [options, setOptions] = useState<WineSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [inputValue, setInputValue] = useState('');
  // Local state for the venue-user request flow. Admin's CreateWineModal is
  // still owned by the parent, so we don't manage it here.
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestModalQuery, setRequestModalQuery] = useState('');

  // Hold the current venueId in a ref so the long-lived debounced function
  // always reads the latest value without being rebuilt on every render.
  const venueIdRef = useRef(venueId);
  useEffect(() => { venueIdRef.current = venueId; }, [venueId]);

  const debouncedSearch = useRef(
    debounce(async (q: string) => {
      if (q.trim().length < 2) {
        setOptions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setSearchFailed(false);
      try {
        const results = await wineService.search(q, 10, venueIdRef.current);
        setOptions(results);
      } catch {
        setOptions([]);
        setSearchFailed(true);
      } finally {
        setLoading(false);
      }
    }, 300),
  ).current;

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleInputChange = useCallback((_: React.SyntheticEvent, newInput: string) => {
    setInputValue(newInput);
    setSearchFailed(false);
    if (newInput.trim().length < 2) {
      setOptions([]);
      setLoading(false);
      debouncedSearch.cancel();
      return;
    }
    setLoading(true);
    debouncedSearch(newInput);
  }, [debouncedSearch]);

  const createOption: CreateOption = {
    _id: CREATE_OPTION_ID,
    name: venueRequestMode ? '+ Request new wine' : '+ Create new wine',
    __isCreate__: true,
  };

  // Append the sentinel only when creation is allowed for this user.
  const dropdownOptions: DropdownOption[] = canCreate
    ? [...options, createOption]
    : options;

  const noOptionsText = (() => {
    if (inputValue.trim().length < 2) return 'Type at least 2 characters to search';
    if (loading) return 'Searching…';
    if (searchFailed) return 'Search failed — check your connection';
    return 'No wines found';
  })();

  // Wine (full populated record) → WineSearchResult (lightweight projection).
  // Mirror of the shape returned by GET /admin/wine/search.
  const toSearchResult = (wine: Wine): WineSearchResult => ({
    _id: wine._id,
    name: wine.name,
    displayVintage: wine.displayVintage,
    vintageType: wine.vintageType,
    wineDisplay: {
      producerName: wine.wineName?.producer?.name,
      regionName: wine.wineName?.region?.name,
      color: wine.wineName?.color,
    },
  });

  return (
    <>
    <Autocomplete<DropdownOption>
      value={value}
      inputValue={inputValue}
      options={dropdownOptions}
      loading={loading}
      disabled={disabled}
      filterOptions={x => x}
      getOptionLabel={option => isCreateOption(option) ? '' : (option?.name ?? '')}
      isOptionEqualToValue={(option, val) => {
        if (isCreateOption(option) || isCreateOption(val as DropdownOption)) return false;
        return (option as WineSearchResult)._id === (val as WineSearchResult)._id;
      }}
      onInputChange={handleInputChange}
      onChange={(_e, newValue) => {
        if (!newValue) {
          onChange(null);
          setOptions([]);
          return;
        }
        if (isCreateOption(newValue)) {
          // Don't update selected value — open the appropriate modal flow.
          // VENUE_USER → internal RequestWineModal; ADMIN → parent-provided
          // onCreateNew callback (CreateWineModal).
          if (venueRequestMode) {
            setRequestModalQuery(inputValue);
            setRequestModalOpen(true);
          } else if (adminCreateMode) {
            onCreateNew?.(inputValue);
          }
          return;
        }
        onChange(newValue as WineSearchResult);
        setOptions([]);
      }}
      noOptionsText={noOptionsText}
      renderOption={(props, option) => {
        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };

        if (isCreateOption(option)) {
          return (
            <li key={key} {...rest}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, width: '100%', color: 'primary.main' }}>
                <CreateIcon fontSize="small" />
                <Typography variant="body2" fontWeight={600} color="primary.main">
                  {venueRequestMode ? 'Request new wine…' : 'Create new wine…'}
                </Typography>
              </Box>
            </li>
          );
        }

        const wine = option as WineSearchResult;
        const color = wine.wineDisplay?.color;
        const canonicalColor = normalizeWineColor(color);
        const producer = wine.wineDisplay?.producerName;
        const region = wine.wineDisplay?.regionName;
        const subtitle = [producer, region].filter(Boolean).join(' · ');
        return (
          <li key={key} {...rest}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5, width: '100%' }}>
              <Box
                sx={{
                  width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                  backgroundColor: canonicalColor ? wineColorDotFor(color) : NEUTRAL_WINE_DOT,
                  opacity: canonicalColor ? 1 : 0.35,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {wine.name ?? '—'}
                </Typography>
                {subtitle && (
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
          </li>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required
          error={error || searchFailed}
          helperText={
            helperText !== undefined
              ? helperText
              : !value && inputValue.trim().length < 2
                ? 'Type at least 2 characters to search'
                : undefined
          }
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={16} sx={{ mr: 0.5 }} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
    {venueRequestMode && venueId && (
      <RequestWineModal
        open={requestModalOpen}
        venueId={venueId}
        initialQuery={requestModalQuery}
        onClose={() => setRequestModalOpen(false)}
        onCreated={(wine) => {
          // Select the freshly-requested wine on the form. The wine carries
          // needsReview=true; the parent form treats it like any other
          // selection — it's already linkable.
          onChange(toSearchResult(wine));
          setOptions([]);
          setRequestModalOpen(false);
        }}
      />
    )}
    </>
  );
};

export default WineSearchBox;
